import React from 'react';
import { Code2, Lock, Zap, MessageSquare, Database, Workflow, BarChart3, Webhook } from 'lucide-react';

/**
 * Public SDK docs page at /docs/sdk. Organized by purpose: each domain
 * (WhatsApp Agent, Insights, Webhooks) gets its own section so the surface
 * stays readable as endpoints are added. Examples use generic field names
 * — partners decide their own observation vocabulary.
 */
export const SdkDocsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
            <Code2 size={20} className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold">BRNDSH API — v1</h1>
        </div>
        <p className="text-sm text-gray-500 mb-10">
          Programmatic access to the platform. Build dashboards, automations, and integrations
          on top of your bot, your roster, and your data.
        </p>

        {/* ─── Foundations ─── */}
        <Section title="Authentication" icon={<Lock size={14} />}>
          <p>
            All requests use a per-client API key. Generate one in your marketplace:{' '}
            <strong>Marketplace → AI Assistant → Developer → Create key</strong>. Pass it as a
            Bearer token on every request.
          </p>
          <CodeBlock>
{`curl https://client-api.agentsanywhere.ai/api/sdk/v1/recipients \\
  -H "Authorization: Bearer sk_brndsh_..."`}
          </CodeBlock>
          <p className="text-xs text-gray-500">
            Keys are scoped to one client — every query auto-filters to that client's data.
            The platform stores only a hash; once you create a key, save it immediately. If lost,
            revoke and regenerate.
          </p>
        </Section>

        <Section title="Base URL" icon={<Zap size={14} />}>
          <CodeBlock>https://client-api.agentsanywhere.ai/api/sdk/v1</CodeBlock>
          <p className="text-xs text-gray-500">
            All endpoints return JSON. Standard response shape:{' '}
            <code className="px-1 py-0.5 bg-gray-100 rounded">{'{ data: ..., total?: number }'}</code>.
          </p>
        </Section>

        {/* ─── Domain: WhatsApp Agent ─── */}
        <DomainHeader
          title="WhatsApp Agent"
          icon={<MessageSquare size={16} />}
          description="Manage your messaging roster, read conversations, query structured data, and inspect automated workflows."
        />

        <SubSection title="Recipients" description="Your roster — the end-users (customers, patients, members) registered against a WhatsApp channel.">
          <ApiTable rows={[
            ['GET',    '/recipients',                    'List recipients (filters: status, tag, search, limit, offset)'],
            ['GET',    '/recipients/{id}',               'Fetch a single recipient with full metadata'],
            ['POST',   '/recipients?channel_id=...',     'Add a recipient (requires opt_in_confirmed=true — Meta policy)'],
            ['PATCH',  '/recipients/{id}',               'Update tags, metadata, schedule (timezone, preferred_time_local)'],
          ]} />
        </SubSection>

        <SubSection title="Messages" description="Read full conversation history per recipient, or send a free-form message inside the WhatsApp 24-hour window.">
          <ApiTable rows={[
            ['GET',    '/recipients/{id}/messages',      'Conversation history, chronological, with metadata'],
            ['POST',   '/recipients/{id}/messages',      'Send a free-form message (subject to 24h customer-service window)'],
          ]} />
          <Callout>
            Outside the 24-hour window, free-form sends return <code>422</code> with a hint to use
            an approved template. Template-send via SDK is on the roadmap.
          </Callout>
        </SubSection>

        <SubSection title="Observations" description="Structured data extracted from conversations and forms — the backing store for charts, exports, and analytics. Field names are partner-defined.">
          <ApiTable rows={[
            ['GET', '/recipients/{id}/observations', 'All observations for one recipient (filter by field, date range)'],
            ['GET', '/observations',                  'Cross-recipient query (e.g. one field across the roster)'],
          ]} />
        </SubSection>

        <SubSection title="Workflows" description="Read your automated flows (scheduled check-ins, drip sequences, event-triggered) and per-recipient session progress.">
          <ApiTable rows={[
            ['GET', '/workflows',                       'List workflows (filter: active=true|false)'],
            ['GET', '/workflows/{id}',                  'Workflow definition + trigger + step list'],
            ['GET', '/workflows/{id}/sessions',         'Per-recipient enrollments + progress + last activity'],
          ]} />
        </SubSection>

        {/* ─── Domain: Insights ─── */}
        <DomainHeader
          title="Insights"
          icon={<BarChart3 size={16} />}
          description="Aggregate counters tuned for dashboard rendering — one round trip per page."
        />

        <SubSection title="Stats" description="Pre-computed rollups across recipients, workflows, sessions, and observations.">
          <ApiTable rows={[
            ['GET', '/stats', 'Counts: recipients (total/opted_in/active_24h), workflows, sessions, observations (24h, 7d)'],
          ]} />
        </SubSection>

        {/* ─── Domain: Webhooks (coming) ─── */}
        <DomainHeader
          title="Webhooks"
          icon={<Webhook size={16} />}
          description="Push notifications when something happens — instead of polling for state changes, we POST to your URL."
          badge="Coming v1.1"
        />

        <SubSection title="Subscriptions">
          <p className="text-sm text-gray-700 mb-3">
            Register a URL to receive event payloads. Each subscription has its own signing secret —
            we sign every delivery with HMAC-SHA256 in the <code>X-Brndsh-Signature</code> header so
            you can verify authenticity.
          </p>
          <ApiTable rows={[
            ['POST',   '/webhooks',                'Create a subscription (url, events[], secret returned once)'],
            ['GET',    '/webhooks',                'List active subscriptions for the client'],
            ['DELETE', '/webhooks/{id}',           'Revoke a subscription'],
            ['POST',   '/webhooks/{id}/redeliver', 'Replay a failed event by id'],
          ]} />
        </SubSection>

        <SubSection title="Events" description="The events you can subscribe to. We deliver at-least-once with exponential backoff on non-2xx responses.">
          <EventTable rows={[
            ['recipient.created',           'A recipient was added to your roster (via SDK, dashboard, or workflow opt-in).'],
            ['recipient.opted_out',         'A recipient revoked consent — stop sending.'],
            ['recipient.updated',           'Tags, metadata, or schedule changed.'],
            ['observation.recorded',        'A new structured data point was captured (from a workflow step or form submission).'],
            ['message.inbound',             'A recipient sent a message to your channel.'],
            ['message.outbound.delivered',  'A platform-sent message was delivered to the recipient.'],
            ['message.outbound.failed',     'A platform-sent message failed (provider error, opt-out, window closed, etc.).'],
            ['workflow.session.started',    'A recipient was enrolled into a workflow.'],
            ['workflow.session.completed',  'A recipient finished a workflow.'],
            ['workflow.session.errored',    'A workflow session entered an error state — needs attention.'],
          ]} />
        </SubSection>

        <SubSection title="Payload shape" description="All events share a common envelope; the event-specific data lives in `data`.">
          <CodeBlock>
{`{
  "id": "evt_01HM...",
  "event": "observation.recorded",
  "client_id": "84c2e574-a6fc-4fb4-8ea2-8355501490ba",
  "occurred_at": "2026-05-01T15:23:18.412Z",
  "data": {
    "observation": {
      "id": "...",
      "recipient_id": "...",
      "field": "your_field_name",
      "value": 142,
      "unit": "mg/dL",
      "recorded_at": "2026-05-01T15:23:18Z",
      "workflow_session_id": "...",
      "step_id": "morning_reading"
    }
  }
}`}
          </CodeBlock>
        </SubSection>

        <SubSection title="Verifying the signature">
          <CodeBlock>
{`// Node example
import crypto from 'node:crypto';

function verify(rawBody, signatureHeader, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(rawBody)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(signatureHeader),
  );
}`}
          </CodeBlock>
        </SubSection>

        {/* ─── Footer / reference ─── */}
        <Section title="Quickstart — TypeScript" icon={<Code2 size={14} />}>
          <CodeBlock>
{`async function listRecipients() {
  const res = await fetch(
    'https://client-api.agentsanywhere.ai/api/sdk/v1/recipients?status=opted_in&limit=50',
    {
      headers: { Authorization: \`Bearer \${process.env.BRNDSH_API_KEY}\` },
    }
  );
  if (!res.ok) throw new Error(\`HTTP \${res.status}\`);
  const { data, total } = await res.json();
  return { recipients: data, total };
}`}
          </CodeBlock>
        </Section>

        <Section title="Errors" icon={<Database size={14} />}>
          <p>Standard HTTP status codes. Error body shape:</p>
          <CodeBlock>{'{ "detail": "human-readable message" }'}</CodeBlock>
          <ul className="text-sm space-y-1.5 mt-3">
            <li><code className="px-1 py-0.5 bg-gray-100 rounded text-xs">401</code> — missing or invalid API key</li>
            <li><code className="px-1 py-0.5 bg-gray-100 rounded text-xs">403</code> — key revoked or insufficient scope</li>
            <li><code className="px-1 py-0.5 bg-gray-100 rounded text-xs">404</code> — resource not found (or doesn't belong to your client)</li>
            <li><code className="px-1 py-0.5 bg-gray-100 rounded text-xs">409</code> — conflict (e.g. recipient phone already exists)</li>
            <li><code className="px-1 py-0.5 bg-gray-100 rounded text-xs">422</code> — validation error (e.g. outside the WhatsApp 24-hour window)</li>
            <li><code className="px-1 py-0.5 bg-gray-100 rounded text-xs">502</code> — upstream provider failed (Meta, etc.)</li>
          </ul>
        </Section>

        <Section title="Versioning" icon={<Workflow size={14} />}>
          <p>
            Path-versioned at <code className="px-1 py-0.5 bg-gray-100 rounded">/api/sdk/v1</code>.
            Breaking changes ship under <code className="px-1 py-0.5 bg-gray-100 rounded">/v2</code>;
            v1 stays stable. Additive changes (new endpoints, new optional fields) ship inside v1
            without notice.
          </p>
        </Section>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; icon?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  icon,
  children,
}) => (
  <section className="mb-10">
    <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
      {icon && <span className="text-emerald-600">{icon}</span>}
      {title}
    </h2>
    <div className="text-sm text-gray-700 space-y-3 leading-relaxed">{children}</div>
  </section>
);

const DomainHeader: React.FC<{
  title: string;
  icon: React.ReactNode;
  description: string;
  badge?: string;
}> = ({ title, icon, description, badge }) => (
  <div className="mt-12 mb-6 pb-3 border-b border-gray-200">
    <div className="flex items-center gap-2.5 mb-1.5">
      <span className="text-emerald-600">{icon}</span>
      <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      {badge && (
        <span className="inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 rounded">
          {badge}
        </span>
      )}
    </div>
    <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
  </div>
);

const SubSection: React.FC<{
  title: string;
  description?: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <section className="mb-7">
    <h3 className="text-sm font-semibold text-gray-900 mb-1">{title}</h3>
    {description && <p className="text-xs text-gray-500 mb-3 leading-relaxed">{description}</p>}
    <div className="text-sm text-gray-700 space-y-3 leading-relaxed">{children}</div>
  </section>
);

const Callout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-relaxed">
    {children}
  </div>
);

const CodeBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <pre className="bg-gray-900 text-gray-100 rounded-lg px-4 py-3 text-xs font-sans overflow-x-auto whitespace-pre">
    <code>{children}</code>
  </pre>
);

const ApiTable: React.FC<{ rows: [string, string, string][] }> = ({ rows }) => (
  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <table className="w-full text-xs">
      <thead className="bg-gray-50">
        <tr className="text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
          <th className="px-3 py-2 w-16">Method</th>
          <th className="px-3 py-2 w-72">Path</th>
          <th className="px-3 py-2">Description</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map(([m, p, d], i) => (
          <tr key={i}>
            <td className="px-3 py-2 font-sans">
              <span
                className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  m === 'GET'
                    ? 'bg-emerald-50 text-emerald-700'
                    : m === 'POST'
                    ? 'bg-blue-50 text-blue-700'
                    : m === 'PATCH'
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-red-50 text-red-700'
                }`}
              >
                {m}
              </span>
            </td>
            <td className="px-3 py-2 font-sans text-gray-900">{p}</td>
            <td className="px-3 py-2 text-gray-600">{d}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const EventTable: React.FC<{ rows: [string, string][] }> = ({ rows }) => (
  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <table className="w-full text-xs">
      <thead className="bg-gray-50">
        <tr className="text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
          <th className="px-3 py-2 w-72">Event</th>
          <th className="px-3 py-2">Description</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map(([name, desc], i) => (
          <tr key={i}>
            <td className="px-3 py-2 font-sans text-gray-900">{name}</td>
            <td className="px-3 py-2 text-gray-600">{desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default SdkDocsPage;
